import { Collateral, CollateralStatus, ValidationIssue } from 'api'
import { Locale, Translation } from 'locale'
import React, { useRef } from 'react'
import { Nullable } from 'utils'
import { isNotNil } from 'utils/predicate'
import { SelectboxField } from 'view/components/options'
import { CollateralDialogFields } from './collateralDialogUtils'

interface CollateralStatusSelectboxProps {
  /** draft collateral state (ie: workflow collateral) */
  collateral: Collateral
  onUpdate: (value: CollateralStatus | undefined) => void
  /** original collateral to edit */
  originalCollateral: Nullable<Collateral>
  readOnly: boolean
  validationIssues: ValidationIssue[]
}

const statusTranslations = {
  [CollateralStatus.PENDING]: Translation.TC_PENDING,
  [CollateralStatus.PENDING_FAILED]: Translation.TC_PENDING_FAILED,
  [CollateralStatus.PENDING_AWAITING_PRICE]: Translation.TC_PENDING_AWAITING_PRICE,
  [CollateralStatus.ENABLED]: Translation.TC_ACTIVE,
  [CollateralStatus.DISABLED]: Translation.TC_SUSPENDED,
  [CollateralStatus.READY_FOR_ACTIVATION]: Translation.TC_RFA
}

const getStatus = (translation: Translation) => {
  switch (translation) {
    case Translation.TC_PENDING_FAILED:
      return CollateralStatus.PENDING_FAILED
    case Translation.TC_PENDING:
      return CollateralStatus.PENDING
    case Translation.TC_ACTIVE:
      return CollateralStatus.ENABLED
    case Translation.TC_SUSPENDED:
      return CollateralStatus.DISABLED
    case Translation.TC_RFA:
      return CollateralStatus.READY_FOR_ACTIVATION
    default:
      return undefined
  }
}

export const CollateralStatusSelectbox = ({
  collateral,
  onUpdate,
  originalCollateral,
  readOnly,
  validationIssues
}: CollateralStatusSelectboxProps) => {
  const initialCollateralStatus = useRef(collateral.status)

  const isOriginalCollateralNonPending =
    isNotNil(originalCollateral) && ![CollateralStatus.PENDING, CollateralStatus.PENDING_FAILED].includes(originalCollateral.status)

  return (
    <SelectboxField
      fieldName={CollateralDialogFields.COLLATERAL_STATUS}
      validationIssues={validationIssues}
      label={Locale.translate(Translation.SC_COLLATERAL_STATUS)}
      readOnly={readOnly}
      value={statusTranslations[collateral.status]}
      getOptionText={(option: Translation) => Locale.translate(option)}
      onUpdate={value => onUpdate(getStatus(value))}
      getOptionDisabled={option => {
        switch (option) {
          case Translation.TC_RFA:
            return initialCollateralStatus.current !== CollateralStatus.READY_FOR_ACTIVATION
          case Translation.TC_PENDING:
            return isOriginalCollateralNonPending
          case Translation.TC_PENDING_FAILED:
            return isOriginalCollateralNonPending
          case Translation.TC_PENDING_AWAITING_PRICE:
            return true
          default:
            return false
        }
      }}
      optionKeys={[
        Translation.TC_PENDING_FAILED,
        Translation.TC_PENDING,
        Translation.TC_PENDING_AWAITING_PRICE,
        Translation.TC_ACTIVE,
        Translation.TC_SUSPENDED,
        Translation.TC_RFA
      ]}
    />
  )
}
